import { useCallback } from 'react';
import { UserInterfaceSettings } from '../../state';
import { ListedInstrument, NbSelectItemOption, OrderType, TradingSession } from '../../types';
import { betMarketCodes, usaMarketCodes, xetraFrMarketCodes } from '../estream-mapper/eStreamMapperConsts';

type UseOrderTradingSessionCommonParams = {
  getTradingSessionOptions: (
    instrument: ListedInstrument | null | undefined,
    orderType?: OrderType,
  ) => NbSelectItemOption<TradingSession>[];
  userInterfaceSettings: UserInterfaceSettings;
};

type UseOrderTradingSessionCommonOutput = {
  getInitialTradingSession: (
    instrument: ListedInstrument | null | undefined,
    orderType: OrderType,
  ) => TradingSession | undefined;
};

const getDefaultTradingSessionForMic = (
  instrument: ListedInstrument | null | undefined,
  userInterfaceSettings: UserInterfaceSettings,
): TradingSession | undefined => {
  if (instrument && usaMarketCodes.has(instrument.mic)) {
    return userInterfaceSettings.orderDefaults.USATradingSession;
  } else if (instrument && betMarketCodes.has(instrument.mic)) {
    return userInterfaceSettings.orderDefaults.BETTradingSession;
  } else if (instrument && xetraFrMarketCodes.has(instrument.mic)) {
    return TradingSession.All;
  } else if (instrument) {
    return TradingSession.UnderTrading;
  }

  return undefined;
};

export const useOrderTradingSessionCommon = ({
  getTradingSessionOptions,
  userInterfaceSettings,
}: UseOrderTradingSessionCommonParams): UseOrderTradingSessionCommonOutput => {
  const getInitialTradingSession = useCallback(
    (instrument: ListedInstrument | null | undefined, orderType: OrderType): TradingSession | undefined => {
      const defaultTradingSession = getDefaultTradingSessionForMic(instrument, userInterfaceSettings);
      const tradingSessionOptions = getTradingSessionOptions(instrument, orderType);

      return tradingSessionOptions.some(option => option.id === defaultTradingSession)
        ? defaultTradingSession
        : (tradingSessionOptions[0]?.id as TradingSession | undefined);
    },
    [getTradingSessionOptions, userInterfaceSettings],
  );

  return {
    getInitialTradingSession,
  };
};
